/**
 * Shared route adapter for monograph-style hub apps.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import "../styles/tailwind.css";
import "../styles/monograph-route.css";

import React from "react";
import {
  MonographDemoShell,
  PanelLoadingFallback,
  cssProperties,
  createMonographPanelTokens,
  monographTokenVars,
  type CssCustomProperties,
  type MonographDemoShellProps,
} from "@csk/demo-ui";
import { monographRouteBackground } from "./monograph-route-background.js";

type MonographRouteTheme =
  | MonographDemoShellProps["theme"]
  | Record<string, string>;

export interface MonographRouteAppProps<PanelId extends string> {
  readonly navigation: {
    readonly entries: MonographDemoShellProps<PanelId>["nav"];
    readonly defaultPanelId: PanelId;
    readonly isPanelId: MonographDemoShellProps<PanelId>["isPanelId"];
  };
  readonly theme: MonographRouteTheme;
  readonly surfaceTokens?: CssCustomProperties;
  readonly slug: string;
  readonly title: string;
  readonly eyebrow?: string;
  readonly tagline: string;
  readonly companionPath: string;
  readonly backgroundColor: string;
  readonly backgroundVoid?: string;
  readonly backgroundDeep?: string;
  readonly surfaceTokenPrefix?: string;
  readonly surfaceTokenAliases?: readonly MonographRouteSurfaceTokenAlias[];
  readonly mainRegionId?: string;
  readonly appLabel?: string;
  readonly shortcutsTitle?: string;
  readonly shortcutsDescription?: string;
  readonly recoveryLabel?: string;
  readonly rootDataName?: string;
  readonly currentScript?: string;
  readonly panelSuspenseAccent?: string;
}

export interface MonographRouteSurfaceTokenAlias {
  readonly prefix: string;
  readonly tokens: MonographRouteTokenSource;
}

export type MonographRouteTokenSource = Readonly<
  Record<string, string | undefined>
>;

function resolveMonographRouteTheme(
  theme: MonographRouteTheme,
): MonographDemoShellProps["theme"] & Record<string, string> {
  return createMonographPanelTokens(theme as unknown as Record<string, string>);
}

function defaultAppLabel(title: string): string {
  return title.toUpperCase();
}

function mergeDefinedTokenSource(
  base: MonographDemoShellProps["theme"],
  override: MonographRouteTokenSource,
): Record<string, string | undefined> {
  const merged: Record<string, string | undefined> = {
    ...(base as unknown as Record<string, string | undefined>),
  };
  for (const [name, value] of Object.entries(override)) {
    if (value !== undefined && value !== "") {
      merged[name] = value;
    }
  }
  return merged;
}

export function createMonographRouteSurfaceTokens(
  theme: MonographDemoShellProps["theme"],
  surfaceTokenPrefix: string | undefined,
  surfaceTokenAliases: readonly MonographRouteSurfaceTokenAlias[],
  surfaceTokens: CssCustomProperties | undefined,
): CssCustomProperties {
  const aliasTokens = surfaceTokenAliases.reduce<CssCustomProperties>(
    (tokens, alias) => ({
      ...tokens,
      ...monographTokenVars(
        alias.prefix,
        mergeDefinedTokenSource(theme, alias.tokens),
      ),
    }),
    {},
  );
  return cssProperties(
    {
      "--monograph-route-focus": theme.accent,
    },
    surfaceTokenPrefix === undefined
      ? {}
      : monographTokenVars(
          surfaceTokenPrefix,
          theme as unknown as Record<string, string | undefined>,
        ),
    aliasTokens,
    surfaceTokens,
  );
}

export function MonographRouteApp<PanelId extends string>({
  navigation,
  theme,
  surfaceTokens,
  slug,
  title,
  eyebrow = "INTERACTIVE DEMO",
  tagline,
  companionPath,
  backgroundColor,
  backgroundVoid,
  backgroundDeep,
  surfaceTokenPrefix,
  surfaceTokenAliases = [],
  mainRegionId = `${slug}-main`,
  appLabel = defaultAppLabel(title),
  shortcutsTitle = `${title} shortcuts`,
  shortcutsDescription = `Keyboard commands for moving across the ${title} demo.`,
  recoveryLabel = "Return to thesis",
  rootDataName = slug,
  currentScript = `demo:${slug}`,
  panelSuspenseAccent,
}: MonographRouteAppProps<PanelId>): React.JSX.Element {
  const resolvedTheme = resolveMonographRouteTheme(theme);
  const resolvedPanelSuspenseAccent =
    panelSuspenseAccent ?? resolvedTheme.accent;
  return (
    <MonographDemoShell
      nav={navigation.entries}
      defaultPanelId={navigation.defaultPanelId}
      isPanelId={navigation.isPanelId}
      theme={resolvedTheme}
      title={title}
      eyebrow={eyebrow}
      tagline={tagline}
      companionPath={companionPath}
      mainRegionId={mainRegionId}
      appLabel={appLabel}
      shortcutsTitle={shortcutsTitle}
      shortcutsDescription={shortcutsDescription}
      recoveryLabel={recoveryLabel}
      rootDataName={rootDataName}
      currentScript={currentScript}
      background={monographRouteBackground(
        backgroundColor,
        backgroundVoid ?? resolvedTheme.void,
        backgroundDeep,
      )}
      surfaceTokens={createMonographRouteSurfaceTokens(
        resolvedTheme,
        surfaceTokenPrefix,
        surfaceTokenAliases,
        surfaceTokens,
      )}
      panelSuspenseFallback={
        <PanelLoadingFallback
          accentColor={resolvedPanelSuspenseAccent}
          theme={resolvedTheme}
        />
      }
    />
  );
}

export function createMonographRouteApp<PanelId extends string>(
  props: MonographRouteAppProps<PanelId>,
): () => React.JSX.Element {
  return function ConfiguredMonographRouteApp(): React.JSX.Element {
    return <MonographRouteApp {...props} />;
  };
}
